import { isFilledByUs } from "@/lib/attributeReview/noFieldPick";
import { fieldDescription } from "@/lib/attributeReview/fieldDescription";
import type { MouseEvent } from "react";

import { Button } from "@/components/ui";
import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview.types";
import { isRequiredLevel } from "@/lib/attributeReview/requiredWarning";
import { isSettledGroup } from "@/lib/attributeReview/reviewGroups";
import type { ReviewGroupKey } from "@/lib/attributeReview/reviewRow.types";
import { confidenceView, workChips } from "@/lib/attributeReview/rowChips";
import { cn } from "@/lib/utils";

import { ConfidenceChip } from "./ConfidenceChip";
import { RequiredStar } from "./RequiredStar";
import { SourceCell } from "./SourceCell";
import { SuggestionCell } from "./SuggestionCell";
import { WorkChip } from "./WorkChip";

const QUEUE_GRID =
  "grid grid-cols-[1.5rem_minmax(11rem,1.1fr)_minmax(16rem,2fr)_10rem_11rem] items-center gap-4";

type Props = {
  row: AttributeReviewMappingRow;
  group: ReviewGroupKey;
  variant: "table" | "compact";
  checked: boolean;
  isCursor: boolean;
  isDuplicate: boolean;
  isDraft: boolean;
  notSaved: boolean;
  isQueued: boolean;
  sameLeafLabel?: string;
  onCheck: (id: string, checked: boolean, shiftKey: boolean) => void;
  onOpen: (id: string) => void;
  onApprove: (row: AttributeReviewMappingRow) => void;
  onIgnore: (row: AttributeReviewMappingRow) => void;
  onUndo: (row: AttributeReviewMappingRow) => void;
  onShowToSeller: (row: AttributeReviewMappingRow) => void;
};

const stop = (event: MouseEvent) => event.stopPropagation();

export function ReviewQueueRow({
  row,
  group,
  variant,
  checked,
  isCursor,
  isDuplicate,
  isDraft,
  notSaved,
  isQueued,
  sameLeafLabel,
  onCheck,
  onOpen,
  onApprove,
  onIgnore,
  onUndo,
  onShowToSeller,
}: Props) {
  const id = String(row.id);
  const pending = !isSettledGroup(group);
  const confidence = confidenceView(row);
  const chips = workChips(row, {
    sameLeafLabel,
    isDraft,
    isQueued,
    notSaved,
  });
  const rowClass = cn(
    "cursor-pointer border-b border-slate-100 bg-white px-3 py-3 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800/60",
    isCursor && "shadow-[inset_3px_0_0_0_#6366f1]",
  );

  if (variant === "compact") {
    const label = String(row.sourceAttribute ?? row.id);
    const description = fieldDescription(row);
    const path = row.pathPreview
      ? `${isDuplicate ? `${id} · ` : ""}${String(row.pathPreview)}`
      : `${isDuplicate ? `${id} · ` : ""}${isFilledByUs(row.kind) ? "system field" : "no pick yet"}`;
    return (
      <div
        id={`attribute-review-row-${id}`}
        className={cn(rowClass, "space-y-1")}
        onClick={() => onOpen(id)}
      >
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            className="rounded text-left text-sm font-medium text-slate-900 [overflow-wrap:anywhere] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-100"
          >
            {label}
            <RequiredStar level={row.requirementLevel} />
          </button>
          <ConfidenceChip view={confidence} short />
        </div>
        {description && (
          <p className="text-xs text-slate-500 [overflow-wrap:anywhere] dark:text-slate-400">
            {description}
          </p>
        )}
        <div className="flex items-start justify-between gap-3">
          <p className="font-mono text-xs text-slate-500 [overflow-wrap:anywhere] dark:text-slate-400">
            {path}
          </p>
          <div className="flex flex-wrap justify-end gap-1">
            {chips.map((chip) => (
              <WorkChip key={chip.text} chip={chip} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id={`attribute-review-row-${id}`}
      className={cn(rowClass, QUEUE_GRID)}
      onClick={() => onOpen(id)}
    >
      <span className="flex items-center" onClick={stop}>
        {pending && (
          <input
            type="checkbox"
            aria-label={`Select ${String(row.sourceAttribute ?? id)}`}
            checked={checked}
            onChange={() => undefined}
            onClick={(event) => onCheck(id, !checked, event.shiftKey)}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
        )}
      </span>
      <SourceCell row={row} isDuplicate={isDuplicate} />
      <SuggestionCell row={row} chips={chips} />
      <ConfidenceChip view={confidence} />
      <div className="flex items-center justify-end gap-2" onClick={stop}>
        {group === "ready" && (
          <Button size="sm" onClick={() => onApprove(row)}>
            Approve
          </Button>
        )}
        {pending &&
          group !== "hidden" &&
          !isRequiredLevel(row.requirementLevel) && (
            <Button size="sm" variant="outline" onClick={() => onIgnore(row)}>
              Ignore
            </Button>
          )}
        {group === "ignored" && (
          <Button size="sm" variant="outline" onClick={() => onUndo(row)}>
            Undo
          </Button>
        )}
        {group === "hidden" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onShowToSeller(row)}
          >
            Show to seller
          </Button>
        )}
      </div>
    </div>
  );
}
